import { Theme } from '@radix-ui/themes';
import { StatsigProvider } from '@statsig/react-bindings';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import Cookies from 'js-cookie';
import absoluteUrl from 'next-absolute-url';
import App, { type AppContext, type AppInitialProps, type AppProps } from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Script from 'next/script';
import NextNProgress from 'nextjs-progressbar';
import { useEffect } from 'react';
import { ThemeProvider } from 'styled-components';

import { ClientErrorBoundary, GlobalStyles, Layout, OnboardingPopup } from '@/components';
import { GeoProvider, useTrackSessions } from '@/hooks';
import { GlobalStateProvider } from '@/hooks/useGlobalState.hooks';

import {
  CONNECTING_TO_CONTENTFUL,
  CONNECTING_TO_REDIS,
  CookieKey,
  // ROUTES,
  // MAX_PHONE_WIDTH,
  DEVELOPMENT_HOST,
  EUCountryCodes,
  FALLBACK_PMT_LANGUAGE_CODE,
  LocalStorageKey,
  NON_POPUP_PATHS,
  REDIS_APP_KEY_PREFIX,
  REDIS_PMC_APP_KEY_PREFIX,
  REDIS_SEO_APP_KEY_PREFIX,
  SUPPORTED_LOCALES,
  UTMKeys,
  serverSharedData,
  theme,
} from '@/constants';

import {
  checkIsPreviewModeRequest,
  checkPageWithLanguageCode,
  excludeUndefinedKey,
  gtmVerificationPopupViews,
  gtmViewBrowserVerification,
  onboardingPopUpDataModify,
  revertMasterTranslationLanguageCode,
  sendSentryError,
  setHasVisited,
  setLocalStorageByKey,
} from '@/utils';

import {
  getAuthenticatorAccessToken,
  getFooter,
  getHeader,
  getOnboardingPopUps,
  getPmtOnboardingPopUp,
  getUserInfo,
} from '@/fetchers';

import {
  type IAppBanner,
  type IFooter,
  type IHeader,
  type IUserInfo,
  LanguageCode,
  OnboardingStepName,
} from '@/interfaces';

import { getAppBanner } from '@/fetchers/contentfulAPI/appBanner.fetchers';
import { PmtSignUpStep } from '@/interfaces/tracking';
import { firebaseMessaging } from '@/lib/firebase/messaging';
import { GoogleAuthWrapper } from '@/modules/pmt/components/GoogleAuthWrapper';
import { AdsProvider } from '@/modules/pmt/context/Ads.context';
import { EmailSyncProvider } from '@/modules/pmt/context/EmailSync.context';
import { loadDayjsLocale } from '@/utils';
import { refreshToken as tokenRefresher } from '@/utils/auth/tokenRefresher';
import { detectMobileOS } from '@/utils/detectOs.utils';
import { pmtPopUpDataModify } from '@/utils/graphqlDataModify.utils';
import { checkIsMultiLanguagePage } from '@/utils/path.utils';
import { ServerCookieManager } from '@/utils/serverCookies.utils';
import { fetchTranslationsData } from '@/utils/translation.utils';
import { usePathname } from 'next/navigation';

const VerificationPopup = dynamic(async () => import('@/components/VerificationPopup'), { ssr: false });

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();

function MyApp({ Component, pageProps }: AppProps) {
  const {
    isLocalhost,
    userInfo,
    isLoggedIn,
    isTrackingPage,
    isMultiLanguagePage,
    origin,
    header,
    footer,
    isErrorPage,
    onboardingPopUp,
    currentStep,
    webVersionCached,
    isWebpSupported,
    browserUuid,
    languageCode,
    headerInfo,
    authenticatorAccessToken,
    appBanner,
    deviceInfo,
    defaultTranslations,
    initialTranslations,
  } = pageProps;

  console.debug(webVersionCached);

  if (headerInfo) {
    console.debug(headerInfo);
  }

  polyfillCountryFlagEmojis();

  const router = useRouter();
  const { query } = router;
  const pathname = usePathname();
  const { triggerGtmSessions } = useTrackSessions(isLoggedIn);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount to set initial token
  useEffect(() => {
    setLocalStorageByKey(LocalStorageKey.authenticatorAccessToken, authenticatorAccessToken);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount for initial GTM user identification
  useEffect(() => {
    if (isLoggedIn && userInfo?.id) {
      window.gtag?.('set', { user_id: userInfo.id });
    } else {
      window.gtag?.('set', { user_id: null });
    }
  }, []);

  useEffect(() => {
    const removeCookies = () => {
      Cookies.remove(CookieKey.NewBrowserUuid);
      Cookies.remove(CookieKey.UserEmail);
    };

    window.addEventListener('beforeunload', removeCookies);

    return () => {
      window.removeEventListener('beforeunload', removeCookies);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = languageCode;
    loadDayjsLocale(languageCode);
  }, [languageCode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: triggerGtmSessions and isMultiLanguagePage intentionally omitted — pathname change is the trigger signal
  useEffect(() => {
    // For track-my-parcel page, we handle in useEmailSyncAccounts
    // so that we can get the number of email accounts
    const isTrackMyParcelPage = checkPageWithLanguageCode(pathname, 'track-my-parcel');
    if (isTrackMyParcelPage) return;
    triggerGtmSessions(isMultiLanguagePage);
  }, [pathname]);

  const renderPopups = () => {
    const isNonPopupPath = NON_POPUP_PATHS.includes(pathname);
    const shouldNotRenderPopup = [isErrorPage, isNonPopupPath, isTrackingPage].some(Boolean);

    if (shouldNotRenderPopup) return null;
    const cookieVerificationStatus = Cookies.get(CookieKey.VerificationStatus);
    const differentBrowser = cookieVerificationStatus && JSON.parse(cookieVerificationStatus);
    if (differentBrowser) {
      isMultiLanguagePage ? gtmViewBrowserVerification() : gtmVerificationPopupViews();
    }
    Cookies.remove(CookieKey.VerificationStatus);

    return (
      <>
        {!isMultiLanguagePage && onboardingPopUp && (
          <OnboardingPopup {...onboardingPopUp} differentBrowser={differentBrowser} />
        )}
        {differentBrowser && <VerificationPopup />}
      </>
    );
  };

  // Ref: https://developers.google.com/tag-platform/devguides/cross-domain
  const GTMScriptComponent = () => {
    return (
      <>
        <Script
          id='gtag-script'
          strategy='afterInteractive'
          src={`https://www.googletagmanager.com/gtag/js?id=${publicRuntimeConfig.GA4_PM_ID}`}
        />
        <Script
          id='gtag-consent'
          strategy='afterInteractive'
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
          dangerouslySetInnerHTML={{
            __html: `
              // Define dataLayer and the gtag function.
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}

              gtag('consent', 'default', {
                'ad_user_data': 'granted',
                'ad_personalization': 'granted',
                'ad_storage': 'granted',
                'analytics_storage': 'granted',
              });
              
              gtag('consent', 'default', {
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
                'ad_storage': 'denied',
                'analytics_storage': 'denied',
                'region': [
                  'US-CA',
                  ${EUCountryCodes.map((countryCode) => `'${countryCode}'`).join(',')}
                ],
              });

              function setGaCookie(key, value) {
                if (!key || !value) return;
                document.cookie = key + '=' + value + '; path=/; max-age=86400';
              }

              gtag('get', '${publicRuntimeConfig.GA4_PM_ID}', 'session_id', function(id) { 
                setGaCookie('${CookieKey.Ga4SessionId}_${publicRuntimeConfig.GA4_PM_ID}', id);
              });

              gtag('get', '${publicRuntimeConfig.GA4_PMC_ID}', 'session_id', function(id) { 
                setGaCookie('${CookieKey.Ga4SessionId}_${publicRuntimeConfig.GA4_PMC_ID}', id);
              });

              // Note: We only need to use one representative container (GA4 Measurement ID) to get the client_id.
              // The client_id value is shared across all containers on the same domain, as it is stored in the _ga cookie.
              // Therefore, there is no need to store or retrieve client_id separately for each container ID.

              gtag('get', '${publicRuntimeConfig.GA4_PM_ID}', 'client_id', function(id) { 
                setGaCookie('${CookieKey.Ga4ClientId}', id);
              });

              gtag('set', 'linker', {
                'domains': ['${publicRuntimeConfig.origin}']
              });
              gtag('js', new Date());
              gtag('config', '${publicRuntimeConfig.GA4_PM_ID}');
            `,
          }}
        />

        <Script
          id='gtm-init'
          strategy='afterInteractive'
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
          dangerouslySetInnerHTML={{
            __html: `
            (function(w,d,s,l,i){
              w[l]=w[l]||[];
              function gtag(){w[l].push(arguments);}

              w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;

              j.addEventListener('load', function() {
                var _ge = new CustomEvent('gtm_loaded', { bubbles: true });
                d.dispatchEvent(_ge);
              });

              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${publicRuntimeConfig.GTM_ID}');
          `,
          }}
        />
      </>
    );
  };

  const renderPageLayout = () => {
    const shouldRenderLayout = !NON_POPUP_PATHS.includes(pathname);
    if (!shouldRenderLayout) return <Component {...pageProps} />;
    return (
      <Layout
        footer={footer}
        header={header}
        onlyLogo={pageProps?.landingPageData?.onlyLogo}
        appBanner={appBanner}
        deviceInfo={deviceInfo}
      >
        <>
          <NextNProgress color={theme.colors.blue} options={{ showSpinner: false }} />
          {!isLocalhost && !isTrackingPage && <GTMScriptComponent />}
          <Component {...pageProps} />
        </>
      </Layout>
    );
  };

  firebaseMessaging.initialize();

  setHasVisited();

  const utmData: Record<UTMKeys, unknown> | null = excludeUndefinedKey({
    [UTMKeys.utmCampaign]: query[UTMKeys.utmCampaign],
    [UTMKeys.utmContent]: query[UTMKeys.utmContent],
    [UTMKeys.utmMedium]: query[UTMKeys.utmMedium],
    [UTMKeys.utmSource]: query[UTMKeys.utmSource],
    [UTMKeys.utmTerm]: query[UTMKeys.utmTerm],
  });

  useEffect(() => {
    const handleCaptureLoadingResourceError = (event: ErrorEvent) => {
      if (!event.target || typeof event.target !== 'object') {
        return;
      }

      // biome-ignore lint/suspicious/noExplicitAny: DOM NamedNodeMap has dynamic shape
      const shouldIgnoreError = (nodeName: string, nodeAttributes: any) => {
        const CAPTURE_NODE_NAMES = ['img', 'script'];
        const isIgnoreError = !CAPTURE_NODE_NAMES.includes(nodeName.toLowerCase());

        if (isIgnoreError) {
          return true;
        }

        const isImage = nodeName.toLocaleLowerCase() === 'img';

        const isLazyLoading = isImage && nodeAttributes?.loading?.value === 'lazy';
        if (isLazyLoading) {
          return true;
        }

        const isHighPriority = isImage && nodeAttributes?.fetchpriority?.value === 'high';
        if (isHighPriority) {
          return true;
        }

        const CONTROLLABLE_DOMAINS = [
          'images.ctfassets.net', // Contentful
          'assets.parcelperform.com', // S3
          'cms-assets.parcelperform.com', // NEO CMS
          'tracking-for-business.parcelperform.com', // SME
        ];
        const imageUrl = nodeAttributes?.src?.value;
        const isUncontrollableDomain =
          isImage && imageUrl && !CONTROLLABLE_DOMAINS.some((domain) => imageUrl.includes(domain));

        // Ignore images from uncontrollable domains
        if (isUncontrollableDomain) {
          return true;
        }

        return false;
      };

      // biome-ignore lint/suspicious/noExplicitAny: event target cast for DOM attribute access
      const target = event.target as any;
      const nodeAttributes = target.attributes;
      const nodeName = (event.target as Node).nodeName;
      const isIgnoreError = shouldIgnoreError(nodeName, nodeAttributes);
      if (isIgnoreError) {
        return;
      }

      // biome-ignore lint/suspicious/noExplicitAny: extra data has dynamic shape
      const extraData: any = {
        nodeName,
      };

      Object.keys(nodeAttributes).forEach((key) => {
        extraData.attributes = {
          ...extraData.attributes,
          [nodeAttributes[key].name]: nodeAttributes[key].value,
        };
      });

      sendSentryError('Loading Resource 404s', extraData);
    };

    document.body.addEventListener(
      'error',
      handleCaptureLoadingResourceError,
      true, // useCapture - necessary for resource loading errors
    );

    return () => {
      document.body.removeEventListener('error', handleCaptureLoadingResourceError);
    };
  }, []);

  const statsigUser = {
    userID: userInfo?.id ?? 'anonymous',
    ...(userInfo && {
      custom: {
        email_sync_opt_in: String(userInfo.emailSyncOptIn),
        newsletter_opt_in: String(userInfo.newsletterOptIn),
        subscribe_to_email_summary: String(userInfo.subscribeToEmailSummary),
        opt_in_date: userInfo.optInDate ?? undefined,
        sign_up_source: userInfo.signUpSource ?? undefined,
        user_source: userInfo.userSource ?? undefined,
        last_time_enable_ems: userInfo.lastTimeEnableEms ?? undefined,
        last_time_disable_ems: userInfo.lastTimeDisableEms ?? undefined,
        email_sync_reauthentication_count: userInfo.emailSyncReauthenticationCount,
        push_notification_opt_in: userInfo.pushNotificationOptIn,
      },
    }),
  };

  return (
    <StatsigProvider
      sdkKey={publicRuntimeConfig.statsigClientKey}
      user={statsigUser}
      options={{
        environment: { tier: publicRuntimeConfig.statsigEnvironment },
        plugins: [new StatsigSessionReplayPlugin(), new StatsigAutoCapturePlugin()],
      }}
    >
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Theme>
          <ClientErrorBoundary>
            <GlobalStateProvider
              origin={publicRuntimeConfig.origin || origin}
              isLoggedIn={isLoggedIn}
              userInfo={userInfo}
              isFollowingLinkedIn={true}
              showPopUp={false}
              showPMTPopUp={false}
              isFromGetNotifiedBtn={false}
              isFromEmailSyncBtn={false}
              isFromDelayedShipmentBtn={false}
              isFromJoinUsBtn={false}
              isFromBasicDetailsBtn={false}
              currentStep={currentStep}
              pmtSignUpStep={PmtSignUpStep.SignUp}
              isNextButtonClicked={false}
              isErrorPage={isErrorPage}
              isTrackingPage={isTrackingPage}
              aboutYouClicksLabel={onboardingPopUp?.aboutYouClicksLabel}
              isFollowUsZapierTriggered={false}
              isWebpSupported={isWebpSupported}
              browserUuid={browserUuid}
              utmData={utmData}
              defaultTranslations={defaultTranslations}
              initialTranslations={initialTranslations}
            >
              <GeoProvider>
                <AdsProvider>
                  <EmailSyncProvider>
                    <GoogleAuthWrapper>
                      {renderPopups()}
                      {renderPageLayout()}
                    </GoogleAuthWrapper>
                  </EmailSyncProvider>
                </AdsProvider>
              </GeoProvider>
            </GlobalStateProvider>
          </ClientErrorBoundary>
        </Theme>
      </ThemeProvider>
    </StatsigProvider>
  );
}

interface IAppServerSideProps {
  header: IHeader | null;
  footer: IFooter[] | null;
  // biome-ignore lint/suspicious/noExplicitAny: Contentful popup data has dynamic shape
  onboardingPopUp: any;
  // biome-ignore lint/suspicious/noExplicitAny: Contentful popup data has dynamic shape
  pmtOnboardingPopUp: any;
  // biome-ignore lint/suspicious/noExplicitAny: Contentful popup data has dynamic shape
  pmtEmailSyncOnboardingPopUp: any;
  appBanner: IAppBanner[] | null;
  defaultTranslations: Record<string, string>;
  initialTranslations: Record<string, string>;
}

MyApp.getInitialProps = async (appContext: AppContext): Promise<AppInitialProps> => {
  // biome-ignore lint/suspicious/noExplicitAny: function cast for conditional redis assignment
  let getServerPropsCached: (pageURL: string) => Promise<string | null> = (() => null) as any;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic server props data shape
  let cacheServerProps: (pageURL: string, data: Record<string, any>) => void = () => null;

  if (!serverRuntimeConfig.useDev) {
    const redisUtils = await import('@/utils/redis.utils');

    getServerPropsCached = redisUtils.getServerPropsCached;
    cacheServerProps = redisUtils.cacheServerProps;
  }

  const { host } = absoluteUrl(appContext.ctx.req);
  const isWebpSupported =
    appContext.ctx.req?.headers['accept']?.split(',').includes('image/webp') ||
    appContext.ctx.req?.headers['accept'] === '*/*';

  const { ctx } = appContext;

  const isPreviewMode = checkIsPreviewModeRequest(ctx.query);
  const serverCookieManager = new ServerCookieManager(ctx.req?.headers.cookie || '', ctx.res);

  const accessToken = serverCookieManager.getCookie(CookieKey.AccessToken);
  const refreshToken = serverCookieManager.getCookie(CookieKey.RefreshToken);
  const userId = serverCookieManager.getCookie(CookieKey.UserId);

  const [appProps, getUserInfoResponse, authenticatorAccessToken] = await Promise.all([
    App.getInitialProps(appContext),
    (async () => {
      // This is to force user logout for the case user has logged in before Phoenix 04 is deployed
      if (userId && !accessToken) {
        console.info('GetUserInfo: No access token found, remove user cookies');
        serverCookieManager.removeUserCookies();
        return null;
      }

      if (!accessToken) return null;

      try {
        const data = await getUserInfo({ accessToken });
        return data;
        // biome-ignore lint/suspicious/noExplicitAny: catch clause
      } catch (error: any) {
        if (error?.status === 401) {
          // Handle 401 error
          if (!refreshToken) {
            console.info('GetUserInfo: No refresh token found, remove user cookies');
            serverCookieManager.removeUserCookies();
            return null;
          }

          try {
            // Try to refresh the token
            const refreshedToken = await tokenRefresher(refreshToken);

            serverCookieManager.setCookie(CookieKey.AccessToken, refreshedToken.access_token);

            // Retry getUserInfo with new access token
            return await getUserInfo({ accessToken: refreshedToken.access_token });
          } catch (_refreshError) {
            // If refresh fails, clear all tokens using ServerCookieManager
            serverCookieManager.removeUserCookies();
            return null;
          }
        }
        return null;
      }
    })(),
    getAuthenticatorAccessToken(),
  ]);

  const isUserInfoValid = getUserInfoResponse !== null && 'id' in getUserInfoResponse;

  const isLoggedIn = isUserInfoValid && !!getUserInfoResponse.email && !!getUserInfoResponse.verificationStatus;

  serverSharedData.isLoggedIn = isLoggedIn;
  serverSharedData.authenticatorAccessToken = authenticatorAccessToken;

  // This is for data protection
  let userInfo: IUserInfo | null = null;
  let currentStep: OnboardingStepName = OnboardingStepName.joinUs;

  if (isUserInfoValid) {
    userInfo = getUserInfoResponse;

    if (userInfo && isLoggedIn) {
      currentStep = OnboardingStepName.followUs;
    }
  }

  const pathNoParams = ctx.asPath?.split('?')[0];
  const { origin } = absoluteUrl(ctx.req);
  const isErrorPage = ['/404', '/500', '/503'].includes(ctx.pathname);
  const isTrackingPage = ['/tracking'].includes(ctx.pathname);
  const isMultiLanguagePage = checkIsMultiLanguagePage(pathNoParams || '');

  const isNonPopupPath = NON_POPUP_PATHS.includes(ctx.pathname);

  const appServerProps: IAppServerSideProps = {
    header: null,
    footer: null,
    onboardingPopUp: null,
    pmtOnboardingPopUp: null,
    pmtEmailSyncOnboardingPopUp: null,
    appBanner: null,
    defaultTranslations: {},
    initialTranslations: {},
  };

  //! TODO: BELOW IS TEMPORARY CONSOLE.LOG FOR TESTING CLOUDFLARE HEADER, DELETE LATER
  //! NOTE: console.log is enable for all env if matches condition
  const isPpUserFromGoogle =
    userInfo?.email?.includes('@parcelperform.com') && userInfo.authenticationMethod === 'Google'; // no need interface since just testing
  const isSecretPath = ctx.asPath === '/events/packagefulfillmentlogisticsanddeliveryexpo';
  const headerInfo = isPpUserFromGoogle && isSecretPath && ctx.req?.headers;

  // Get the user agent from the request headers
  const userAgent = ctx.req?.headers['user-agent'] || '';

  // Detect the device info
  const deviceInfo = detectMobileOS(userAgent);
  const browserUuid = serverCookieManager.getCookie(CookieKey.BrowserUuid) || '';

  const basePageProps = {
    pageProps: {
      // biome-ignore lint/suspicious/noExplicitAny: appProps shape is dynamic at runtime
      ...(appProps as any).pageProps,
      ...appServerProps,
      isLocalhost: host === DEVELOPMENT_HOST,
      userInfo,
      isLoggedIn,
      origin,
      isErrorPage,
      isTrackingPage,
      isMultiLanguagePage,
      currentStep,
      webVersionCached: CONNECTING_TO_CONTENTFUL,
      isWebpSupported,
      browserUuid,
      languageCode: LanguageCode.English,
      headerInfo,
      authenticatorAccessToken,
      deviceInfo,
      defaultTranslations: {},
      initialTranslations: {},
    },
  };

  if (isNonPopupPath) {
    return basePageProps;
  }

  const { articleTypeSlug, slug } = ctx.query;

  // SEO pages (e.g. /vi/track-global) use a locale prefix but are not PMT pages.
  // Detect them so we can resolve header/footer in the correct language without
  // flipping isMultiLanguagePage (which would switch to the PMT footer).
  const isSeoPage = SUPPORTED_LOCALES.includes(articleTypeSlug as string) && (slug as string)?.startsWith('track-');

  const languageCode = isErrorPage
    ? LanguageCode.English
    : (isMultiLanguagePage && (articleTypeSlug as LanguageCode)) ||
      (isSeoPage && revertMasterTranslationLanguageCode(articleTypeSlug as string)) ||
      LanguageCode.English;
  const redisAppKey = isSeoPage
    ? REDIS_SEO_APP_KEY_PREFIX + languageCode
    : isMultiLanguagePage
      ? REDIS_APP_KEY_PREFIX + languageCode
      : REDIS_PMC_APP_KEY_PREFIX;
  basePageProps.pageProps.languageCode = languageCode;

  // Get header, footer, popup regardless of page url
  // app-en, app-zh-cn, app-zh-tw
  const serverPropsCached: IAppServerSideProps = JSON.parse((await getServerPropsCached(redisAppKey)) || 'null');
  if (serverPropsCached) {
    appServerProps.header = serverPropsCached.header;
    appServerProps.footer = serverPropsCached.footer;
    appServerProps.onboardingPopUp = serverPropsCached.onboardingPopUp;
    appServerProps.pmtOnboardingPopUp = serverPropsCached.pmtOnboardingPopUp;
    appServerProps.pmtEmailSyncOnboardingPopUp = serverPropsCached.pmtEmailSyncOnboardingPopUp;
    basePageProps.pageProps.webVersionCached = CONNECTING_TO_REDIS;
    appServerProps.appBanner = serverPropsCached.appBanner;
  } else {
    const inAppActions = [
      getHeader({ isPreviewMode, languageCode, slug: ctx.asPath?.split('?')[0] || 'app-header' }),
      getFooter({ isPreviewMode, languageCode, isMultiLanguagePage, slug: ctx.asPath?.split('?')[0] || '/app-footer' }),
      isMultiLanguagePage
        ? getPmtOnboardingPopUp({ isPreviewMode, languageCode })
        : getOnboardingPopUps({ isPreviewMode, slug: ctx.asPath?.split('?')[0] || 'app-popups' }),
      getAppBanner({ isPreviewMode, languageCode, slug: ctx.asPath?.split('?')[0] || 'app-banner' }),
    ];

    // biome-ignore lint/suspicious/noExplicitAny: Promise.all returns tuple with mixed types
    const inAppResponse: any = await Promise.all(inAppActions);
    appServerProps.header = inAppResponse[0];
    appServerProps.footer = inAppResponse[1];

    if (!isMultiLanguagePage) {
      appServerProps.onboardingPopUp = inAppResponse[2];
    } else if (inAppResponse[2]) {
      appServerProps.pmtOnboardingPopUp = inAppResponse[2].pmtOnboardingPopUp;
      appServerProps.pmtEmailSyncOnboardingPopUp = inAppResponse[2].pmtEmailSyncOnboardingPopUp;
    }

    appServerProps.appBanner = inAppResponse[3];

    if (!isPreviewMode) {
      cacheServerProps(redisAppKey, appServerProps);
    }
  }

  if (!isMultiLanguagePage) {
    appServerProps.onboardingPopUp = onboardingPopUpDataModify(appServerProps.onboardingPopUp);
  } else {
    // Randomize pmtOnboardingPopUp for a/b testing
    appServerProps.pmtOnboardingPopUp = pmtPopUpDataModify(appServerProps.pmtOnboardingPopUp);
    appServerProps.pmtEmailSyncOnboardingPopUp = pmtPopUpDataModify(appServerProps.pmtEmailSyncOnboardingPopUp);
  }

  // Fetch translations data
  appServerProps.defaultTranslations = await fetchTranslationsData(FALLBACK_PMT_LANGUAGE_CODE);
  if (languageCode === FALLBACK_PMT_LANGUAGE_CODE) {
    appServerProps.initialTranslations = appServerProps.defaultTranslations;
  } else {
    appServerProps.initialTranslations = await fetchTranslationsData(languageCode);
  }

  try {
    decodeURIComponent(ctx.asPath || '');
  } catch {
    return {
      pageProps: {
        ...basePageProps.pageProps,
        header: appServerProps.header,
        footer: null,
        errorCode: 404,
      },
    };
  }

  if (isErrorPage) {
    return {
      pageProps: {
        ...basePageProps.pageProps,
        header: appServerProps.header,
      },
    };
  }

  if (isTrackingPage) {
    return {
      pageProps: {
        ...basePageProps.pageProps,
        header: appServerProps.header,
        footer: appServerProps.footer,
      },
    };
  }

  return {
    pageProps: {
      ...basePageProps.pageProps,
      ...appServerProps,
    },
  };
};

export default MyApp;
